import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { adminApi } from '../../services/api';
import type { ConversationDetail } from '../../types/admin';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SentimentBadge from '../../components/admin/SentimentBadge';
import SentimentTrendChart from '../../components/admin/SentimentTrendChart';
import { ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

// Format date/time in user's local timezone
const formatLocalDateTime = (dateString: string): string => {
  // Ensure we're parsing the date as UTC if it doesn't have timezone info
  const date = dateString.endsWith('Z') ? new Date(dateString) : new Date(dateString + 'Z');
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

export default function ConversationDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [conversation, setConversation] = useState<ConversationDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);
  const [expandedMessagePrompts, setExpandedMessagePrompts] = useState<Set<string>>(new Set());
  const [messagesToShow, setMessagesToShow] = useState(20);
  const matchedKeywords = (location.state as any)?.matchedKeywords || [];
  const messageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    if (sessionId) {
      loadConversation();
    }
  }, [sessionId]);

  useEffect(() => {
    // Scroll to first matched message if keywords exist
    if (matchedKeywords.length > 0 && conversation) {
      const firstMatch = conversation.messages.find(m => 
        matchedKeywords.some((keyword: string) => 
          m.content.toLowerCase().includes(keyword.toLowerCase())
        )
      );
      if (firstMatch && messageRefs.current[firstMatch.id]) {
        messageRefs.current[firstMatch.id]?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }
  }, [conversation, matchedKeywords]);

  const highlightKeywords = (text: string, keywords: string[]): JSX.Element => {
    if (!keywords || keywords.length === 0) {
      return <>{text}</>;
    }

    // Create regex pattern for all keywords
    const pattern = keywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const regex = new RegExp(`(${pattern})`, 'gi');
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, index) => {
          const isMatch = keywords.some(k => k.toLowerCase() === part.toLowerCase());
          return isMatch ? (
            <mark key={index} className="bg-yellow-200 font-semibold">
              {part}
            </mark>
          ) : (
            <span key={index}>{part}</span>
          );
        })}
      </>
    );
  };

  useEffect(() => {
    if (sessionId) {
      loadConversation();
    }
  }, [sessionId]);

  const loadConversation = async () => {
    if (!sessionId) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await adminApi.getConversationDetail(sessionId);
      setConversation(data);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load conversation';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="p-6" role="main" aria-label="Conversation error">
        <div className="text-center py-12">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" aria-hidden="true" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">Error loading conversation</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <div className="mt-6 flex gap-4 justify-center">
            <button
              onClick={() => navigate('/admin/conversations')}
              className="text-indigo-600 hover:text-indigo-900"
            >
              ← Back to Conversations
            </button>
            <button
              onClick={loadConversation}
              className="text-indigo-600 hover:text-indigo-900"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="p-6" role="main" aria-label="Conversation not found">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900">Conversation not found</h3>
          <button
            onClick={() => navigate('/admin/conversations')}
            className="mt-4 text-indigo-600 hover:text-indigo-900"
          >
            ← Back to Conversations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" role="main" aria-label="Conversation detail">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/admin/conversations')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Conversations
          </button>
          <button
            onClick={loadConversation}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-wise hover:text-wise-dark border border-wise rounded-lg hover:bg-wise/5 disabled:opacity-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {conversation.userName}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Session: {conversation.sessionId}
            </p>
            {(conversation.systemPromptTemplateName || conversation.systemPrompt) && (
              <div className="mt-2 flex items-center gap-2">
                {conversation.systemPromptTemplateName && (
                  <>
                    <span className="text-xs text-gray-500">Template:</span>
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-wise/10 text-wise">
                      {conversation.systemPromptTemplateName}
                    </span>
                  </>
                )}
                {conversation.systemPrompt && (
                  <button
                    onClick={() => setShowSystemPrompt(!showSystemPrompt)}
                    className="text-xs text-wise hover:text-wise-dark underline ml-2"
                  >
                    {showSystemPrompt ? 'Hide' : 'View'} Full Prompt
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">
              {conversation.messages.length} messages
            </div>
            <div className="text-sm text-gray-600">
              Started {formatLocalDateTime(conversation.createdAt)}
            </div>
          </div>
        </div>

        {/* System Prompt Display */}
        {showSystemPrompt && conversation.systemPrompt && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-blue-900">System Prompt Used</h3>
              <button
                onClick={() => setShowSystemPrompt(false)}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                ✕
              </button>
            </div>
            <div className="bg-white rounded p-3 text-sm text-gray-800 whitespace-pre-wrap font-mono text-xs border border-blue-100 max-h-96 overflow-y-auto">
              {conversation.systemPrompt}
            </div>
          </div>
        )}

        {/* Overall Sentiment */}
        {conversation.overallSentiment && (
          <div className="mt-4 bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-medium text-gray-900">Overall Sentiment</h3>
                <div className="mt-1">
                  <SentimentBadge
                    classification={conversation.overallSentiment.classification}
                    score={conversation.overallSentiment.averageScore}
                    size="md"
                  />
                </div>
              </div>
              {conversation.overallSentiment.hasNegativeMessages && (
                <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800">
                  ⚠️ Contains Negative Sentiment
                </span>
              )}
            </div>

            {/* Sentiment Trend Chart */}
            {conversation.overallSentiment.trend && conversation.overallSentiment.trend.length > 1 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Sentiment Over Time</h4>
                <SentimentTrendChart trend={conversation.overallSentiment.trend} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Message Timeline */}
      <div className="space-y-4" role="feed" aria-label="Conversation messages">
        {/* Load More Button (top) */}
        {conversation.messages.length > messagesToShow && (
          <div className="text-center">
            <button
              onClick={() => setMessagesToShow(prev => prev + 20)}
              className="px-4 py-2 text-sm text-wise hover:text-wise-dark border border-wise rounded-lg hover:bg-wise/5"
            >
              Load {Math.min(20, conversation.messages.length - messagesToShow)} More Messages 
              ({conversation.messages.length - messagesToShow} older messages)
            </button>
          </div>
        )}
        
        {conversation.messages
          .slice(-messagesToShow) // Get last N messages
          .reverse() // Show newest first
          .map((message) => {
          const hasKeywordMatch = matchedKeywords.length > 0 && 
            matchedKeywords.some((keyword: string) => 
              message.content.toLowerCase().includes(keyword.toLowerCase())
            );

          return (
            <div
              key={message.id}
              ref={(el) => (messageRefs.current[message.id] = el)}
              className={`flex ${
                message.role === 'user'
                  ? 'justify-start'
                  : message.role === 'assistant'
                  ? 'justify-end'
                  : 'justify-center'
              }`}
            >
              <div
                className={`max-w-3xl rounded-lg px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-gray-100 text-gray-900'
                    : message.role === 'assistant'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-yellow-50 text-yellow-900 text-sm'
                } ${hasKeywordMatch ? 'ring-2 ring-yellow-400 ring-offset-2' : ''}`}
              >
                {/* Role Badge */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wide opacity-75">
                    {message.role}
                  </span>
                  <span className="text-xs opacity-75">
                    {formatLocalDateTime(message.createdAt)}
                  </span>
                </div>

                {/* Message Content with Keyword Highlighting */}
                <div className="whitespace-pre-wrap">
                  {highlightKeywords(message.content, matchedKeywords)}
                </div>

                {/* Sentiment Info (for user messages) */}
                {message.role === 'user' && message.sentimentClassification && (
                  <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between">
                    <SentimentBadge
                      classification={message.sentimentClassification}
                      score={message.sentimentScore ?? undefined}
                      size="sm"
                    />
                    {message.sentimentKeywords && message.sentimentKeywords.length > 0 && (
                      <div className="text-xs opacity-75" title={`Sentiment keywords: ${message.sentimentKeywords.join(', ')}`}>
                        Keywords: {message.sentimentKeywords.join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Token Usage and System Prompt (for AI messages) */}
                {message.role === 'assistant' && (
                  <div className="mt-2 pt-2 border-t border-indigo-500">
                    <div className="flex items-center justify-between text-xs opacity-75">
                      {message.tokensUsed && <span>Tokens: {message.tokensUsed}</span>}
                      {message.systemPrompt && (
                        <button
                          onClick={() => {
                            const newSet = new Set(expandedMessagePrompts);
                            if (newSet.has(message.id)) {
                              newSet.delete(message.id);
                            } else {
                              newSet.add(message.id);
                            }
                            setExpandedMessagePrompts(newSet);
                          }}
                          className="text-indigo-200 hover:text-white underline ml-2"
                        >
                          {expandedMessagePrompts.has(message.id) ? 'Hide' : 'View'} Prompt
                        </button>
                      )}
                    </div>
                    {expandedMessagePrompts.has(message.id) && message.systemPrompt && (
                      <div className="mt-2 bg-indigo-700 rounded p-2 text-xs font-mono whitespace-pre-wrap max-h-60 overflow-y-auto">
                        {message.systemPromptTemplateName && (
                          <div className="mb-2 pb-2 border-b border-indigo-500 text-indigo-200">
                            Template: {message.systemPromptTemplateName}
                          </div>
                        )}
                        {message.systemPrompt}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
